import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Knowledge.css';

interface KnowledgeDocument {
  id: number;
  title: string;
  description: string;
  file: string;
  file_type: string;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

const DocumentsList: React.FC = () => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await api.get('/knowledge/documents/');
      setDocuments(res.data.results || res.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      if (!title) {
        // Auto-fill title from filename
        setTitle(e.target.files[0].name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('description', description);

      await api.post('/knowledge/documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Reset form and refresh list
      setTitle('');
      setDescription('');
      setFile(null);
      setShowUploadForm(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/knowledge/documents/${id}/`);
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return '📄';
      case 'docx':
      case 'doc':
        return '📝';
      case 'txt':
        return '📃';
      default:
        return '📁';
    }
  };

  if (loading) {
    return <div className="knowledge-container"><div className="knowledge-loading">Loading...</div></div>;
  }

  return (
    <div className="knowledge-container">
      <div className="knowledge-header">
        <div className="header-left">
          <Link to="/knowledge" className="back-link">&larr; Knowledge Base</Link>
          <h1>Documents</h1>
          <p className="knowledge-subtitle">Reference documents for Hank & Laura</p>
        </div>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="btn-action btn-blue"
        >
          {showUploadForm ? 'Cancel' : '+ Upload Document'}
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <form onSubmit={handleUpload} className="upload-form">
          <h3>Upload New Document</h3>
          <div className="form-group">
            <label htmlFor="file">File (PDF, Word, or Text)</label>
            <input
              type="file"
              id="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.txt"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="docTitle">Title</label>
            <input
              type="text"
              id="docTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>
          <div className="form-group">
            <label htmlFor="docDescription">Description</label>
            <textarea
              id="docDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's in this document?"
              rows={2}
            />
          </div>
          <button
            type="submit"
            disabled={uploading || !file}
            className="btn-action btn-green"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </form>
      )}

      {/* Documents List */}
      <div className="documents-list">
        {documents.length === 0 ? (
          <div className="no-documents">
            <p>No documents uploaded yet</p>
            <p className="hint">Upload PDFs, Word docs, or text files for Hank & Laura to reference</p>
          </div>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="document-card">
              <div className="document-icon">{getFileTypeIcon(doc.file_type)}</div>
              <div className="document-info">
                <h3 className="document-title">{doc.title}</h3>
                {doc.description && <p className="document-description">{doc.description}</p>}
                <p className="document-meta">
                  {doc.file_type.toUpperCase()} &bull; Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                  {doc.uploaded_by_name && ` by ${doc.uploaded_by_name}`}
                </p>
              </div>
              <div className="document-actions">
                <a
                  href={doc.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-view"
                >
                  View
                </a>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="btn-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentsList;
